import { GAMEPLAY_CONFIG } from '../../app/config';
import type { RandomSource } from '../../services/RandomSource';

export type PowerUpType = 'ammo' | 'life';

export type PowerUpSpawnContext = {
  activeCount: number;
  ammo: number;
  maxAmmo: number;
  lives: number;
  maxLives: number;
};

export type PowerUpSpawnDebug = {
  elapsedMs: number;
  nextSpawnAtMs: number;
  timeSinceAmmoMs: number;
  timeSinceLifeMs: number;
  ammoWeight: number;
  lifeWeight: number;
};

const between = (random: RandomSource, min: number, max: number): number =>
  min + random.next() * (max - min);

export class PowerUpSpawnManager {
  private elapsedMs = 0;
  private nextSpawnAtMs = 0;
  private lastAmmoAtMs = 0;
  private lastLifeAtMs = 0;
  private lastType?: PowerUpType;
  private ammoWeight = GAMEPLAY_CONFIG.powerUps.ammo.baseWeight;
  private lifeWeight = GAMEPLAY_CONFIG.powerUps.life.baseWeight;

  public constructor(private readonly random: RandomSource) {
    this.reset();
  }

  public reset(): void {
    this.elapsedMs = 0;
    this.lastAmmoAtMs = 0;
    this.lastLifeAtMs = 0;
    this.lastType = undefined;
    this.nextSpawnAtMs = between(
      this.random,
      GAMEPLAY_CONFIG.powerUps.firstSpawnDelayMs.min,
      GAMEPLAY_CONFIG.powerUps.firstSpawnDelayMs.max,
    );
  }

  public update(deltaMs: number, context: PowerUpSpawnContext): PowerUpType | undefined {
    this.elapsedMs += Math.max(0, deltaMs);
    if (
      context.activeCount >= GAMEPLAY_CONFIG.powerUps.maximumActive ||
      this.elapsedMs < this.nextSpawnAtMs
    ) {
      return undefined;
    }
    const ammoRatio = context.maxAmmo === 0 ? 0 : context.ammo / context.maxAmmo;
    this.ammoWeight = GAMEPLAY_CONFIG.powerUps.ammo.baseWeight;
    this.lifeWeight = GAMEPLAY_CONFIG.powerUps.life.baseWeight;
    if (ammoRatio <= 0.25) this.ammoWeight *= 1.7;
    if (context.ammo === 0) this.ammoWeight *= 1.8;
    if (context.lives === 1) this.lifeWeight *= 1.6;
    if (context.lives >= context.maxLives) this.lifeWeight *= 0.08;
    if (this.lastType === 'ammo') this.ammoWeight *= 0.55;
    if (this.lastType === 'life') this.lifeWeight *= 0.45;
    const ammoPity =
      context.ammo === 0 &&
      this.elapsedMs - this.lastAmmoAtMs >= GAMEPLAY_CONFIG.powerUps.ammo.pityThresholdMs;
    const type: PowerUpType = ammoPity
      ? 'ammo'
      : this.random.next() < this.ammoWeight / (this.ammoWeight + this.lifeWeight)
        ? 'ammo'
        : 'life';
    this.lastType = type;
    if (type === 'ammo') this.lastAmmoAtMs = this.elapsedMs;
    else this.lastLifeAtMs = this.elapsedMs;
    this.nextSpawnAtMs =
      this.elapsedMs +
      between(
        this.random,
        GAMEPLAY_CONFIG.powerUps.intervalMs.min,
        GAMEPLAY_CONFIG.powerUps.intervalMs.max,
      );
    return type;
  }

  public defer(): void {
    this.nextSpawnAtMs = Math.min(this.nextSpawnAtMs, this.elapsedMs + 1_000);
  }

  public get debug(): PowerUpSpawnDebug {
    return {
      elapsedMs: this.elapsedMs,
      nextSpawnAtMs: this.nextSpawnAtMs,
      timeSinceAmmoMs: this.elapsedMs - this.lastAmmoAtMs,
      timeSinceLifeMs: this.elapsedMs - this.lastLifeAtMs,
      ammoWeight: this.ammoWeight,
      lifeWeight: this.lifeWeight,
    };
  }
}
